import { FC, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "../../hooks/use-user";
import { Icon } from "./ui";
import { SectionLink } from "./SectionLink";
import { useAuthModal } from "./auth/AuthModalContext";

const navLinks = [
  { label: "Features", section: "categories" },
  { label: "Practice Tests", section: "practice-preview" },
  { label: "How It Works", section: "how-it-works" },
  { label: "Pricing", section: "pricing" },
  { label: "FAQs", section: "faqs" },
];

type BrandMarkProps = { light?: boolean; asLink?: boolean; to?: string };

export const BrandMark: FC<BrandMarkProps> = ({
  light,
  asLink = true,
  to = "/",
}) => {
  const content = (
    <>
      <span
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          light
            ? "bg-surface-container-lowest text-inverse-surface"
            : "bg-primary text-on-primary"
        }`}
      >
        <Icon name="school" className="text-[20px]" />
      </span>
      <span
        className={`font-headline-sm text-lg sm:text-headline-sm tracking-tight ${
          light ? "text-surface-container-lowest" : "text-primary"
        }`}
      >
        PassEnglishTest
      </span>
    </>
  );

  return asLink ? (
    <Link to={to} className="flex items-center gap-2.5 sm:gap-3 shrink-0">
      {content}
    </Link>
  ) : (
    <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">{content}</div>
  );
};

export const PublicHeader: FC = () => {
  const user = useUser();
  const { pathname } = useLocation();
  const { openAuth } = useAuthModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = pathname === "/";

  // Logged-in users go straight to their dashboard
  useEffect(() => {
    if (user) {
      window.location.href = "/dashboard";
    }
  }, [user]);

  // Close the mobile menu on navigation and on Escape
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const openFromMenu = (view: "signin" | "signup") => {
    setMenuOpen(false);
    openAuth(view);
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-surface-container-lowest/90 backdrop-blur-md shadow-[0_1px_8px_rgba(0,0,0,0.04)] font-body-md text-on-surface">
      <div className="h-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between gap-3 sm:gap-6">
        <BrandMark />

        <nav className="hidden xl:flex items-center gap-8" aria-label="Main">
          <SectionLink
            section="top"
            aria-current={isHome ? "page" : undefined}
            className={`font-label-md text-label-md transition-colors ${
              isHome
                ? "text-primary font-bold"
                : "text-on-surface-variant hover:text-on-surface"
            }`}
          >
            Home
          </SectionLink>
          {navLinks.map((link) => (
            <SectionLink
              key={link.section}
              section={link.section}
              className="font-label-md text-label-md text-on-surface-variant hover:text-on-surface transition-colors"
            >
              {link.label}
            </SectionLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <button
            type="button"
            onClick={() => openAuth("signin")}
            className="hidden sm:inline-flex font-label-md text-label-md text-on-surface-variant hover:text-on-surface transition-colors px-3 py-2"
          >
            Log In
          </button>
          <button
            type="button"
            onClick={() => openAuth("signup")}
            className="hidden xs:inline-flex items-center justify-center font-label-md text-label-md text-on-primary bg-primary hover:bg-primary-container px-3.5 sm:px-5 py-2.5 rounded-xl shadow-md transition-all whitespace-nowrap"
          >
            Get Started<span className="hidden sm:inline">&nbsp;Free</span>
          </button>
          <button
            type="button"
            onClick={() => openAuth("signin")}
            aria-label="Account"
            className="hidden sm:flex w-8 h-8 rounded-full bg-primary items-center justify-center"
          >
            <Icon name="person" className="text-on-primary text-[18px]" />
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="public-mobile-menu"
            className="xl:hidden w-10 h-10 rounded-xl flex items-center justify-center text-on-surface hover:bg-surface-container-low transition-colors"
          >
            <Icon name={menuOpen ? "close" : "menu"} className="text-[24px]" />
          </button>
        </div>
      </div>

      {/* Mobile / tablet menu */}
      <div
        id="public-mobile-menu"
        className="xl:hidden bg-surface-container-lowest shadow-lg max-h-[calc(100dvh-5rem)] overflow-y-auto"
        hidden={!menuOpen}
      >
        <nav
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-6 pt-2 flex flex-col"
          aria-label="Mobile"
        >
          {[{ label: "Home", section: "top" }, ...navLinks].map((link) => (
            <SectionLink
              key={link.section}
              section={link.section}
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between py-3.5 font-label-lg text-label-lg text-on-surface border-b border-outline-variant/40 hover:text-primary transition-colors"
            >
              {link.label}
              <Icon
                name="chevron_right"
                className="text-[20px] text-on-surface-variant"
              />
            </SectionLink>
          ))}
          <Link
            to="/contact-us"
            onClick={() => setMenuOpen(false)}
            className="flex items-center justify-between py-3.5 font-label-lg text-label-lg text-on-surface border-b border-outline-variant/40 hover:text-primary transition-colors"
          >
            Contact
            <Icon
              name="chevron_right"
              className="text-[20px] text-on-surface-variant"
            />
          </Link>
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 pt-5">
            <button
              type="button"
              onClick={() => openFromMenu("signin")}
              className="h-12 rounded-xl bg-surface-container hover:bg-surface-container-high text-primary font-label-lg text-label-lg transition-colors"
            >
              Log In
            </button>
            <button
              type="button"
              onClick={() => openFromMenu("signup")}
              className="h-12 rounded-xl bg-primary hover:bg-primary-container text-on-primary font-label-lg text-label-lg shadow-md transition-colors"
            >
              Get Started Free
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};
